import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { CheckIcon } from "@lucide/icons";

import type { OpenAPI } from "simplydo/interfaces";
import { PushNotification } from "simplydo/interfaces/other";
import { RichTextViewer } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Modal } from "simplydo/react/ui/modal";

import actions from "actions";
import { websocketApi } from "api";
import { useAppSelector } from "store";
import {
  initNotifications,
  setupNotifications,
  supportsPush,
} from "utils/pushMessaging";
import util from "utils/utils";

import { ToastContainer } from "components/lib/Toast";

import ModalCarousel from "./lib/ModalCarousel";

const StyledAchievementModal = styled.div`
  width: 450px;
  max-width: 90%;
  margin: 0 auto;
  > div {
    color: black;
    font-size: 1.3em;
    border-radius: 7.5px;
    padding: 20px;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    .confetti-spawner {
      width: 1px;
      height: 1px;
      display: block;
    }
    .congratulations-title {
      font-weight: bold;
      color: gray;
      margin-bottom: 15px;
    }
    .achievement-icon {
      line-height: 80px;
      font-size: 80px;
      margin: 20px 0;
      position: relative;
    }
    .achievement-name {
      font-size: 1.3em;
      font-weight: bold;
      display: block;
      margin-bottom: 5px;
    }
    .achievement-description {
      font-size: 0.9em;
      color: #a5a5a5;
      display: block;
      margin-bottom: 5px;
    }
    .achievement-actions {
      display: flex;
      margin-top: 30px;
      width: 100%;
      justify-content: flex-end;
      .ui.button {
        margin-left: 5px;
      }
    }
  }
`;

type IMessage = {
  message: string;
  thread: string;
  user: OpenAPI.Schemas["User"];
};

type IAchievement = {
  name: string;
  description: string;
  icon: string;
  key: string;
};

const EventSubscriptions = () => {
  const dispatch = useDispatch();

  const user = useAppSelector((state) => state.user);
  const messageThreads = useAppSelector(
    (state) => state.messages.messageThreads,
  );
  const onMessageThreadSelected = useCallback(
    (thread: OpenAPI.Schemas["MessageThread"]) =>
      dispatch(actions.messages.selectThread(thread)),
    [dispatch],
  );
  const userId = user?._id;
  const navigate = useNavigate();

  const [activeAchievement, setActiveAchievement] = useState<IAchievement>({
    name: "",
    description: "",
    icon: "",
    key: "",
  });
  const [achievementModalOpen, setAchievementModalOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const achievementSubscription = websocketApi.subscribe(
      `unlockedAchievement-${userId}`,
      (achievement: IAchievement) => {
        if (achievement.key) {
          setActiveAchievement(achievement);
          setAchievementModalOpen(true);
        }
      },
    );
    return () => achievementSubscription.unsubscribe();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    // // @ts-ignore
    const messageSubscription = websocketApi.subscribe(
      `newMessage-${userId}`,
      (message: IMessage) => {
        if (message.message && message.thread) {
          const thread = messageThreads.find((t) => t._id === message.thread);
          toast((t) => (
            <ToastContainer
              onClick={() => {
                if (thread) onMessageThreadSelected(thread);
                toast.dismiss(t.id);
              }}
            >
              <div className="info-container">
                <span>
                  <b>{message.user.profile.firstName}</b> sent you a message
                </span>
                <span className="meta">&quot;{message.message}&quot;</span>
              </div>
            </ToastContainer>
          ));
        }
      },
    );
    return () => messageSubscription.unsubscribe();
  }, [messageThreads, onMessageThreadSelected, userId]);

  const pushHandler = useCallback(
    (notification: PushNotification) => {
      if (supportsPush() && Notification.permission === "granted") {
        setupNotifications();
      }
      // We may manually choose to surpress some notifications; For example, direct messages are already shown as toasts, therefore we hide the double toast
      if (
        notification.title &&
        !notification?.extra?.suppressInAppNotification
      ) {
        toast((t) => (
          <ToastContainer
            onClick={() => {
              if (notification?.extra?.path) {
                navigate(notification?.extra?.path);
              }
              toast.dismiss(t.id);
            }}
          >
            <div className="info-container">
              <span>{notification.title}</span>
              <span className="meta">{notification.message}</span>
            </div>
          </ToastContainer>
        ));
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (!userId) return;
    initNotifications();
    const notificationSubscription = websocketApi.subscribe(
      `pushNotification-${userId}`,
      pushHandler,
    );
    return notificationSubscription.unsubscribe;
  }, [pushHandler, userId]);

  const [fullScreenAnnouncements, setFullScreenAnnouncements] = useState<
    OpenAPI.Schemas["Announcement"][]
  >([]);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  useEffect(() => {
    if (!userId) return;
    // // @ts-ignore
    const fullScreenAnnouncementSubscription = websocketApi.subscribe(
      `fullScreenAnnouncement-${userId}`,
      (announcement: OpenAPI.Schemas["Announcement"]) => {
        setFullScreenAnnouncements((prev) => [...prev, announcement]);
        setAnnouncementModalOpen(true);
      },
    );
    return () => fullScreenAnnouncementSubscription.unsubscribe();
  }, [userId]);

  return (
    <div>
      <Modal
        basic
        open={achievementModalOpen}
        onClose={() => setAchievementModalOpen(false)}
      >
        <StyledAchievementModal>
          <div>
            <span id="achievementAwarded" />
            <span className="congratulations-title">
              Congratulations, you unlocked an achievement!
            </span>
            <span
              role="img"
              aria-label={activeAchievement.name}
              className="achievement-icon"
              // eslint-disable-next-line react/no-danger -- No better way to render these
              dangerouslySetInnerHTML={{ __html: activeAchievement.icon }}
            />
            <span className="achievement-name">{activeAchievement.name}</span>
            <span className="achievement-description">
              {activeAchievement.description}
            </span>
            <div className="achievement-actions">
              <ButtonLink
                size="small"
                onClick={() => setAchievementModalOpen(false)}
                to="/achievements"
              >
                View all achievements
              </ButtonLink>
              <Button
                size="small"
                onClick={() => setAchievementModalOpen(false)}
                color="primary"
              >
                <Icon icon={CheckIcon} />
                Done
              </Button>
            </div>
          </div>
        </StyledAchievementModal>
      </Modal>

      <ModalCarousel
        title={`Received ${util.pluralise(fullScreenAnnouncements.length, "announcement", "announcements")}`}
        screens={fullScreenAnnouncements.map((announcement) => ({
          title: announcement.subject,
          content: (
            <div>
              {announcement.body ? (
                <RichTextViewer value={announcement.body} />
              ) : (
                <p>{announcement.text}</p>
              )}
              {announcement?.attachments &&
                announcement.attachments.length > 0 && (
                  <>
                    <H5>Attachments</H5>
                    {announcement.attachments.map((attachment) => (
                      <a
                        href={attachment.url}
                        key={attachment.name}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={attachment.name}
                        className="ui small blue button"
                        style={{ borderRadius: 5 }}
                      >
                        <i className="paperclip icon"></i>
                        {attachment.name}
                      </a>
                    ))}
                  </>
                )}
            </div>
          ),
        }))}
        actions={[
          {
            label: "Got it",
            icon: CheckIcon,
            primary: true,
            onClick: () => {
              setFullScreenAnnouncements([]);
              setAnnouncementModalOpen(false);
            },
          },
        ]}
        open={announcementModalOpen}
        onClose={() => setAnnouncementModalOpen(false)}
      />
    </div>
  );
};

export default EventSubscriptions;
